import type { ReactNode } from 'react';
import type { BlockField, BlockStyle, HeaderSection } from '@/blocks/domain/types';
import type { TemplateLogoAlignment, TemplateLogoSize } from '@/templates/domain/types/TemplateLogo';
import type { DocumentContext } from '../../../domain/DocumentContext';

export type FieldsBlockAriaLabel = 'block-header' | 'block-customer_block' | 'block-signature' | 'block-custom';

/**
 * The blocks that persist the section model. A signature block draws sections too, but only when it
 * carries them (`authoredSections`): one saved before LLAMS-699 has none and keeps the flat half-width flow.
 */
export const SECTIONED_ARIA_LABELS: ReadonlySet<FieldsBlockAriaLabel> = new Set([
  'block-header',
  'block-customer_block',
  'block-custom',
  'block-signature'
]);

export interface FieldsBlockRenderProps {
  fields: BlockField[];
  style: BlockStyle;
  context: DocumentContext;
  accentColor?: string;
  knownPaths?: ReadonlySet<string>;
  ariaLabel: FieldsBlockAriaLabel;
  /** Optional company logo above header fields (hasValue: omit when absent). */
  logoUrl?: string | null;
  logoSize?: TemplateLogoSize;
  logoAlignment?: TemplateLogoAlignment;
  /** Header, customer block, Custom and Signature block: the Part 7 section model. Absent/trivial ⇒ the legacy flat layout (unchanged). */
  sections?: HeaderSection[];
  columns?: number;
  /**
   * Quote-canvas chrome drawn over a field (the signature eye). Wrapped in a `position: relative`
   * box and placed absolutely by the caller, so it never changes the field's — or the block's — height.
   */
  renderFieldOverlay?: (field: BlockField) => ReactNode;
  /** Draws a field half-transparent (a seller-hidden line still shown on the internal canvas). */
  isFieldDimmed?: (field: BlockField) => boolean;
}
